import React, {useState} from 'react';
import UniversalProfileImage from 'components/universal/UniversalProfileImage';
import {useNavigate} from 'react-router-dom';
import {centsToString} from 'util/cashFormatter';
import itemTypeToEmoji from 'util/itemTypeToEmoji';
import ItemIcon from 'components/items/ItemIcon';
import getOrdinalSuffix from 'util/language/ordinal';
import PasswordEyeClosedSvg from 'util/svg/password/PasswordEyeClosedSvg';
import CoinyLogoMonocolor from 'util/svg/logo/ShapesOfCoinSvg';

export const NotificationPopupItem = ({notification, onClick, onNotificationRead}) => {
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);

    // Destructure notification properties for easier access
    const {
        b: {topic, seen, createdOn},
        id,
        username,
        message,
        commentId,
        pictureId,
        amount,
        item,
        multiplicity,
        uniqueActors
    } = notification;

    const handleMarkAsRead = (event) => {
        event.stopPropagation(); // Prevent parent click
        onNotificationRead(id, message);
    };

    const notificationClick = () => {
        if (onClick) {
            onClick();
        }
        if (!seen && onNotificationRead) {
            onNotificationRead(id, message);
        }

        switch (topic) {
            case 'ITEM_SOLD':
            case 'ITEM_BOUGHT':
                navigate(`/items-detail/${item}/item-store`);
                break;
            case 'IMAGE_COMMENT':
            case 'COMMENT_REPLY':
            case 'IMAGE_COMMENT_REACTED':
                navigate(`/image/profile/${pictureId}#${commentId}`);
                break;
            case 'IMAGE_OVERTOOK':
                window.location.assign(`/?index=${amount}`);
                break;
            case 'IMAGE_LIKE':
                navigate(`/image/profile/${pictureId}`);
                break;
            default:
                break;
        }
    };

    let displayMessage;
    switch (topic) {
        case 'IMAGE_COMMENT':
            displayMessage = (
                <div className="cut-text-dev">
                    {username} commented on your image: "{message}"
                </div>
            );
            break;
        case 'COMMENT_REPLY':
            displayMessage = (
                <div className="cut-text-dev">
                    {username} replied to your comment with "{message}"
                </div>
            );
            break;
        case 'IMAGE_COMMENT_REACTED':
            displayMessage = (
                <div className="cut-text-dev">
                    {`${username} ${uniqueActors > 1 ? `and ${uniqueActors - 1} other ${uniqueActors > 2 ? 's' : ''}` : ''}`}
                    {`reacted to your comment with ${multiplicity > 1 ? 'total ' : ''}${centsToString(amount)}`}
                </div>
            );
            break;
        case 'ITEM_SOLD':
        case 'ITEM_BOUGHT':
            displayMessage = (
                <div className="cut-text-dev">
                    {username} {uniqueActors > 1 ? `and ${uniqueActors - 1} other ${uniqueActors > 2 ? 's' : ''}` : ''}
                    {topic === 'ITEM_SOLD' ? ' bought from you ' : ' sold you '}
                    {itemTypeToEmoji[item]?.translation || 'item'} {multiplicity > 1 ? `(${multiplicity}x)` : ''}
                    {multiplicity > 1 ? ', each' : ''} for {centsToString(notification.amount || '?')}
                </div>
            );
            break;
        case 'IMAGE_OVERTOOK':
            displayMessage = (
                <div className="cut-text-dev">
                    {message}{multiplicity > 1 ? ` and ${multiplicity - 1} other${multiplicity > 2 ? 's' : ''}` : ''} took {amount + 1}
                    <sup>{getOrdinalSuffix(amount + 1)}</sup> place from your image!
                </div>
            );
            break;
        case 'IMAGE_LIKE':
            displayMessage = (
                <div className="cut-text-dev">
                    {username} liked your image with "{centsToString(amount)}"
                </div>
            );
            break;
        case 'SYSTEM_MESSAGE':
            displayMessage = message;
            break;
        default:
            displayMessage = amount;
            break;
    }

    return (
        <div>
            <div
                className={`notification-item-wrapper cursor-pointer${seen ? '' : ' unread'}`}
                onClick={notificationClick}
                key={id}
            >
                <div className="position-relative">
                    {topic === 'ITEM_SOLD' || topic === 'ITEM_BOUGHT' ? (
                        <ItemIcon lootItem={item} textSize="1.2"/>
                    ) : topic === 'SYSTEM_MESSAGE' ? (
                        <CoinyLogoMonocolor size={'40px'} color={'#266F2D'}/>
                    ) : (
                        <UniversalProfileImage cssClass={'dark-border'} id={pictureId} size="40px"/>
                    )}
                    {topic === 'IMAGE_COMMENT_REACTED' && (
                        <div className="notification-pictogram">
                            {itemTypeToEmoji[item].emoji}
                        </div>
                    )}

                </div>
                <div className="notification-content">{displayMessage}</div>
                {!seen && (
                    <div
                        className="notification-mark-as-read"
                        onClick={handleMarkAsRead}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <PasswordEyeClosedSvg
                            color={isHovered ? 'black' : 'lightgray'}
                            size={'26px'}
                        />
                    </div>
                )}
                <div className="notification-timestamp">
                    <small>{new Date(createdOn).toLocaleString()}</small>
                </div>
            </div>
            <div className="notification-separator"/>
        </div>
    );
};

export default NotificationPopupItem;
